import type {ReactElement, RefObject} from 'react';
import React, {useEffect, useState} from 'react';
import type {FancyTextEffect} from '@Libraries/fancy-text-library';
import {FancyTextListItem} from '@Components/fancy-text-list/components/fancy-text-list-item';
import {getAssetUrl, getReadBucket} from '@Utils/s3.util';
import {ThumbnailItem} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {FancyTextListType} from '@Components/fancy-text-list/fancy-text-list.types';
import {FancyTextType} from '@Components/fancy-text-list/fancy-text-list.types';
import type {FontData} from '@Libraries/font-library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './fancy-text-list.module.scss';

interface FancyTextListProps {
  className?: string;
  data: FancyTextListType;
  selectedId: string;
  onClick(id: string): void;
  parentContainerRef?: RefObject<HTMLDivElement | null>;
  doScrollIntoView?: boolean;
  /**
   * only supported for effects
   */
  showHorizontalVersion?: boolean;
  hasMarginTop?: boolean;
}

export function FancyTextList({hasMarginTop = false, className = '', showHorizontalVersion = false, doScrollIntoView = false, ...props}: FancyTextListProps): ReactElement<any> {
  const [ref, setRefState] = useState<HTMLUListElement | null>(null);
  const [scrollLeftValue, setScrollLeftValue] = useState(0);
  const [scrollIntoView, setScrollIntoView] = useState(false);

  useEffect(() => {
    if (ref && showHorizontalVersion) {
      ref.addEventListener('scroll', handleScroll);

      return (): void => {
        ref.removeEventListener('scroll', handleScroll);
      };
    }
    return (): void => {};
  }, [ref]);

  const handleScroll = (): void => {
    if (ref) {
      setScrollLeftValue(ref.scrollLeft);
    }
  };

  const getFontImgSrc = (fontFamily: string): string => {
    return `${window.PMW.util.repoURL(`fonts/${fontFamily}.png?`, getReadBucket())}`;
  };

  const getStyleImgSrc = (effectId: string): string => {
    return getAssetUrl(`texteffects/small/${effectId}.webp`);
  };

  useEffect((): void => {
    if (ref && doScrollIntoView && !scrollIntoView) {
      setScrollIntoView(true);
    }
  }, [ref]);

  const getDefaultVersion = (id: string, imgSrc: string): ReactElement<any> => {
    return (
      <FancyTextListItem
        isSelected={id === props.selectedId}
        className={styles.listItemContainer}
        key={id}
        id={id}
        imgSrc={imgSrc}
        onClick={(): void => {
          props.onClick(id);
        }}
        doScrollIntoView={scrollIntoView}
      />
    );
  };

  const getHorizontalVersion = (id: string, name: string, imgSrc: string): ReactElement<any> => {
    return (
      <ThumbnailItem
        className={styles.horizontalListItem}
        id={id}
        key={id}
        text={name}
        thumbnail={
          <Thumbnail
            data={{
              type: ThumbnailType.IMAGE,
              imageSrc: imgSrc,
            }}
            className="spacing-p-1"
          />
        }
        isSelected={id === props.selectedId}
        onClick={(): void => {
          props.onClick(id);
        }}
      />
    );
  };

  const prepareListItemForEffects = (fancyTextItem: FancyTextEffect): ReactElement<any> => {
    if (showHorizontalVersion) {
      return getHorizontalVersion(fancyTextItem.id, fancyTextItem.name, getStyleImgSrc(fancyTextItem.id));
    }
    return getDefaultVersion(fancyTextItem.id, getStyleImgSrc(fancyTextItem.id));
  };

  const prepareListItemForFonts = (font: FontData): ReactElement<any> => {
    if (showHorizontalVersion) {
      return getHorizontalVersion(String(font.idfont), font.fontFamily, getFontImgSrc(font.fontFamily));
    }

    return getDefaultVersion(String(font.idfont), getFontImgSrc(font.fontFamily));
  };

  const prepareListItemsForStyles = (data: FancyTextEffect[]): ReactElement<any>[] => {
    return data.map((fancyTextItem: FancyTextEffect) => {
      return prepareListItemForEffects(fancyTextItem);
    });
  };

  const prepareListItemsForFont = (data: FontData[]): ReactElement<any>[] => {
    return data.map((fontItem: FontData) => {
      return prepareListItemForFonts(fontItem);
    });
  };

  const getListItems = (): ReactElement<any>[] => {
    if (props.data.type === FancyTextType.FONT) {
      return prepareListItemsForFont(props.data.fancyTextFonts);
    }

    return prepareListItemsForStyles(props.data.fancyTextStyles);
  };

  const scrollLeft = (): void => {
    if (ref) {
      ref.scrollLeft = ref.scrollLeft - ref.clientWidth + 78;
    }
  };
  const scrollRight = (): void => {
    if (ref) {
      ref.scrollLeft = ref.scrollLeft + ref.clientWidth - 78;
    }
  };

  const getList = (): ReactElement<any> => {
    return (
      <ul
        ref={(e): void => {
          setRefState(e);
        }}
        className={`${className} ${showHorizontalVersion ? `${styles.horizontalContainer} flex-h-row` : ''} ${hasMarginTop ? 'spacing-m-t-2' : ''}`}
      >
        {getListItems()}
      </ul>
    );
  };

  const getHorizontalList = (): ReactElement<any> => {
    return (
      <div className={styles.horizontalListContainer}>
        {scrollLeftValue === 0 ? null : (
          <div className={styles.iconLeft}>
            <Icon onClick={scrollLeft} isDisabled={false} size={IconSize.SIZE_ICON_20} type={IconType.SHADOW} shape={IconShape.CIRCLE} icon="icon-arrow-left" />
          </div>
        )}
        {getList()}
        {ref && scrollLeftValue === ref.scrollWidth - ref.clientWidth ? null : (
          <div className={styles.iconRight}>
            <Icon onClick={scrollRight} size={IconSize.SIZE_ICON_20} type={IconType.SHADOW} shape={IconShape.CIRCLE} icon="icon-arrow-right" />
          </div>
        )}
      </div>
    );
  };

  return showHorizontalVersion ? getHorizontalList() : getList();
}
